import React, { useState, useEffect } from 'react';
import { 
  ArrowLeftIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  UserGroupIcon,
  ClockIcon,
  BanknotesIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { staffApi } from '../../staffApi';

export default function TutorDetail({ tutor, onBack, onSendEmail, onSendWhatsApp }) {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [magicLink, setMagicLink] = useState(null);
  const [creatingMagicLink, setCreatingMagicLink] = useState(false);

  useEffect(() => {
    // Set magic link if it exists in tutor data
    if (tutor && tutor.magic_link) {
      setMagicLink(tutor.magic_link);
    }
  }, [tutor]);

  const handleSendEmail = () => {
    if (emailSubject && emailMessage) {
      onSendEmail(emailSubject, emailMessage);
      setShowEmailModal(false);
      setEmailSubject('');
      setEmailMessage('');
    }
  };

  const handleSendWhatsApp = () => {
    if (whatsAppMessage) {
      onSendWhatsApp(whatsAppMessage);
      setShowWhatsAppModal(false);
      setWhatsAppMessage('');
    }
  };

  const handleCreateMagicLink = async () => {
    setCreatingMagicLink(true);
    try {
      const response = await staffApi.createMagicLinkForTutor(tutor.id);
      if (response.success) {
        setMagicLink(response.data);
        alert('Magic link created successfully!');
      } else {
        alert('Failed to create magic link');
      }
    } catch (error) {
      console.error('Error creating magic link:', error);
      alert('Failed to create magic link');
    } finally {
      setCreatingMagicLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Magic link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const getPaymentMethodDisplay = (bankDetails) => {
    switch (bankDetails.payment_type) {
      case 'PAYPAL':
        return `PayPal: ${bankDetails.email_id}`;
      case 'UPI':
        return `UPI: ${bankDetails.upi_id}`;
      case 'WISE':
        return `Wise: ${bankDetails.membership_id}`;
      default:
        return bankDetails.payment_type;
    }
  };

  const getDegreeDisplay = (degree) => {
    const degreeMap = {
      'BACHELORS': 'Bachelor\'s',
      'MASTERS': 'Master\'s',
      'DOCTORATE': 'Doctorate',
      'DIPLOMA': 'Diploma',
      'POST-DOCTORATE': 'Post-Doctorate'
    };
    return degreeMap[degree] || degree;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center">
              <div className="flex-shrink-0 h-16 w-16">
                <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-xl font-medium text-indigo-600">
                    {tutor.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
              </div>
              <div className="ml-6">
                <h1 className="text-2xl font-bold text-gray-900">{tutor.name}</h1>
                <p className="text-gray-600">{tutor.email}</p>
                <div className="flex items-center mt-1 text-sm text-gray-500">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {tutor.city}, {tutor.state} {tutor.country}
                </div>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowEmailModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              Send Email
            </button>
            <button
              onClick={() => setShowWhatsAppModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PhoneIcon className="h-4 w-4 mr-2" />
              Send WhatsApp
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-sm text-gray-900">{tutor.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Gender</label>
                <p className="text-sm text-gray-900">{tutor.gender}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-sm text-gray-900">{tutor.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <p className="text-sm text-gray-900">{tutor.phone_no}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Timezone</label>
                <p className="text-sm text-gray-900">{tutor.timezone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                <p className="text-sm text-gray-900">{tutor.date_of_birth || 'Not provided'}</p>
              </div>
            </div>
          </div>

          {/* Magic Link */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Magic Link</h3>
              {!magicLink && (
                <button
                  onClick={handleCreateMagicLink}
                  disabled={creatingMagicLink}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {creatingMagicLink ? 'Creating...' : 'Create Magic Link'}
                </button>
              )}
            </div>
            {magicLink ? (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Magic Link URL</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <div className="flex-1 bg-gray-50 border border-gray-300 rounded p-2">
                      <code className="text-xs break-all">{magicLink.url}</code>
                    </div>
                    <button
                      onClick={() => copyToClipboard(magicLink.url)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Copy
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status: </span>
                    <span className={`font-medium ${
                      magicLink.is_usable ? 'text-green-600' : 
                      magicLink.is_expired ? 'text-red-600' : 
                      'text-gray-600'
                    }`}>
                      {magicLink.is_usable ? 'Active' : magicLink.is_expired ? 'Expired' : 'Inactive'}
                    </span>
                  </div>
                  {magicLink.expires_at && (
                    <div>
                      <span className="text-gray-500">Expires: </span>
                      <span className="text-gray-900">{new Date(magicLink.expires_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No magic link created yet. Click "Create Magic Link" to generate one.</p>
            )}
          </div>

          {/* Subjects */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Subjects</h3>
            <div className="flex flex-wrap gap-2">
              {(tutor.subjects || []).map((subject) => (
                <span
                  key={subject.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {subject.name}
                </span>
              ))}
            </div>
          </div>

          {/* Education */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Education</h3>
            <div className="space-y-4">
              {(tutor.educations || []).map((education) => (
                <div key={education.id} className="border-l-4 border-indigo-200 pl-4">
                  <div className="flex items-center">
                    <AcademicCapIcon className="h-5 w-5 text-indigo-500 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      {getDegreeDisplay(education.degree)} in {education.degree_specialization}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{education.institute}</p>
                  <p className="text-sm text-gray-500">Graduated: {education.graduation_year}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Assigned Students */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assigned Students</h3>
            {(tutor.assigned_students || []).length > 0 ? (
              <div className="space-y-3">
                {(tutor.assigned_students || []).map((student) => (
                  <div key={student.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{student.name}</p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                      {student.subject && (
                        <p className="text-xs text-indigo-600">{student.subject}</p>
                      )}
                    </div>
                    <div className="text-right">
                      {student.hours_remaining && (
                        <p className="text-sm text-gray-600">{student.hours_remaining}h remaining</p>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.status === 'ACTIVE' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {student.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No students assigned</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">Base Fee:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {tutor.currency_for_payout} {tutor.base_fee_per_hour}/hour
                </span>
              </div>
              <div className="flex items-center">
                <BanknotesIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">Payment Method:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {tutor.bank_details ? getPaymentMethodDisplay(tutor.bank_details) : 'No payment details available'}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Sessions */}
          {tutor.recent_sessions && tutor.recent_sessions.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Sessions</h3>
              <div className="space-y-3">
                {(tutor.recent_sessions || []).map((session) => (
                  <div key={session.id} className="border-l-4 border-green-200 pl-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{session.student_name}</span>
                      <span className="text-xs text-gray-500">{session.date}</span>
                    </div>
                    <p className="text-sm text-gray-600">{session.subject}</p>
                    <p className="text-xs text-gray-500">{session.duration} minutes</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment History */}
          {tutor.payments && tutor.payments.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Payments</h3>
              <div className="space-y-3">
                {(tutor.payments || []).slice(0, 3).map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {payment.currency} {payment.amount}
                      </p>
                      <p className="text-xs text-gray-500">{payment.payment_method}</p>
                    </div>
                    <span className="text-xs text-gray-500">{payment.payment_date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Email to {tutor.name}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Email subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your message here..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send WhatsApp to {tutor.name}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={whatsAppMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Your WhatsApp message here..."
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendWhatsApp}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  Send WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
